import React, { useState, useRef, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import {
  Card,
  Button,
  Badge,
  SearchInput,
  Select,
  Modal,
  Spinner,
  EmptyState,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Input,
  Tabs,
} from "../../components/UI";
import { leadsAPI, usersAPI, pipelineAPI, productsAPI } from "../../services/api";
import toast from "react-hot-toast";
import {
  HiOutlinePlus,
  HiOutlineFilter,
  HiOutlineDownload,
  HiOutlineUpload,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineUserAdd,
  HiOutlinePhone,
  HiOutlineMail,
  HiOutlineDotsVertical,
  HiOutlineCheck,
  HiOutlineX,
  HiOutlineDocumentText,
  HiOutlineUsers,
  HiOutlineClipboardCheck,
  HiOutlineExclamation,
  HiOutlineCube,
} from "react-icons/hi";
import { format } from "date-fns";

// Phone validation function - validates Indian phone numbers (10 digits)
const validatePhoneNumber = (phone) => {
  if (!phone) return { isValid: false, error: "Phone number is required" };

  // Remove spaces, hyphens, and +91 prefix
  const cleanPhone = phone
    .toString()
    .replace(/[\s\-]/g, "")
    .replace(/^\+91/, "");

  // Check if it's exactly 10 digits
  if (!/^\d{10}$/.test(cleanPhone)) {
    return { isValid: false, error: "Phone must be 10 digits" };
  }

  // Check if it starts with a valid Indian mobile prefix (6-9)
  if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
    return { isValid: false, error: "Phone must start with 6-9" };
  }

  return { isValid: true, cleanPhone };
};

// Name validation function
const validateName = (name) => {
  if (!name) return { isValid: false, error: "Name is required" };

  const cleanName = name.trim();

  if (cleanName.length < 2) {
    return { isValid: false, error: "Name must be at least 2 characters" };
  }

  // Name is allowed to contain letters, numbers, spaces, emojis, and
  // special characters (e.g. "John D.", "O'Brien", "Raj 🌟", "Raj2").
  return { isValid: true, cleanName };
};

// Email validation function
const validateEmail = (email) => {
  if (!email) return { isValid: true, cleanEmail: "" }; // Email is optional

  const cleanEmail = email.trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
    return { isValid: false, error: "Invalid email format" };
  }

  return { isValid: true, cleanEmail };
};

// Status validation function - now validates stages
const validateStage = (stage) => {
  if (!stage) return { isValid: true, cleanStage: "new_lead" }; // Default to 'new_lead' if not provided

  // Normalize the stage: trim, lowercase, and handle underscores/spaces
  const normalizedInput = stage
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "_");

  // Valid stage keys (default stages from pipeline)
  const validStages = [
    "new_lead",
    "contact_attempted",
    "connected",
    "qualified",
    "follow_up_required",
    "meeting_scheduled",
    "proposal_sent",
    "negotiation",
    "converted",
    "lost",
    "not_interested",
  ];

  // Map common variations to stage keys
  const stageMap = {
    new: "new_lead",
    "new lead": "new_lead",
    newlead: "new_lead",
    "contact attempted": "contact_attempted",
    contactattempted: "contact_attempted",
    connected: "connected",
    qualified: "qualified",
    "follow up": "follow_up_required",
    followup: "follow_up_required",
    "follow up required": "follow_up_required",
    "meeting scheduled": "meeting_scheduled",
    meetingscheduled: "meeting_scheduled",
    "proposal sent": "proposal_sent",
    proposalsent: "proposal_sent",
    negotiation: "negotiation",
    converted: "converted",
    lost: "lost",
    "not interested": "not_interested",
    notinterested: "not_interested",
  };

  const normalizedStage = stageMap[normalizedInput] || normalizedInput;

  if (
    !validStages.includes(normalizedStage) &&
    !normalizedStage.includes("_")
  ) {
    // If it doesn't match known stages, still allow it (custom stages)
    return { isValid: true, cleanStage: normalizedInput };
  }

  return { isValid: true, cleanStage: normalizedStage };
};

const statusOptions = [
  { value: "new", label: "New" },
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "follow_up", label: "Follow Up" },
  { value: "cold", label: "Cold" },
  { value: "warm", label: "Warm" },
  { value: "hot", label: "Hot" },
  { value: "converted", label: "Converted" },
];

// Build a compact list of page numbers + "..." ellipses for pagination.
// Always shows first and last page, current page ±1, with ellipses for gaps.
// e.g. page 5 of 20 -> [1, "...", 4, 5, 6, "...", 20]
const buildPageList = (current, total) => {
  const pages = [];
  const set = new Set([1, total, current, current - 1, current + 1]);
  for (const p of set) {
    if (p >= 1 && p <= total) pages.push(p);
  }
  pages.sort((a, b) => a - b);

  const result = [];
  for (let i = 0; i < pages.length; i++) {
    result.push(pages[i]);
    if (i < pages.length - 1 && pages[i + 1] - pages[i] > 1) {
      result.push("...");
    }
  }
  return result;
};

const LeadsList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);

  // State
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [assignmentFilter, setAssignmentFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showSingleAssignModal, setShowSingleAssignModal] = useState(false);
  const [selectedLeadForAssign, setSelectedLeadForAssign] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showBulkSelectModal, setShowBulkSelectModal] = useState(false);
  const [showBulkStageModal, setShowBulkStageModal] = useState(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [bulkSelectCount, setBulkSelectCount] = useState(50);
  const [selectionMode, setSelectionMode] = useState("page"); // 'page' or 'count'
  const [activeTab, setActiveTab] = useState("all");
  const [importData, setImportData] = useState([]);
  const [distributeEqually, setDistributeEqually] = useState(true);
  const [importProductIds, setImportProductIds] = useState([]);
  const [importing, setImporting] = useState(false);
  const [validationResults, setValidationResults] = useState(null);

  // Fetch pipeline stages for dynamic tabs and filters
  const { data: stagesData } = useQuery({
    queryKey: ["pipelineStages"],
    queryFn: async () => {
      const response = await pipelineAPI.getAll();
      return response.data?.data || [];
    },
  });

  // Create stage options for dropdown and mapping
  const stages = stagesData || [];

  // Fetch active products for optional product selection on import
  const { data: productsData } = useQuery({
    queryKey: ["products", "active"],
    queryFn: async () => {
      const response = await productsAPI.getAll({ active: "true" });
      return response.data?.data || [];
    },
  });
  const products = productsData || [];

  const stageOptions = stages.map((stage) => ({
    value: stage.key,
    label: stage.name,
    color: stage.color,
  }));

  // Create a map from stage key to stage info
  const stageMap = stages.reduce((acc, stage) => {
    acc[stage.key] = {
      name: stage.name,
      color: stage.color || "#6B7280",
    };
    return acc;
  }, {});

  // Fetch leads
  const { data: leadsData, isLoading } = useQuery({
    queryKey: ["leads", page, pageSize, activeTab, statusFilter, assignmentFilter, search],
    queryFn: async () => {
      const params = { page, limit: pageSize };
      // activeTab is the single stage filter, applied on the backend so it
      // spans all data — not just the currently loaded page.
      if (activeTab && activeTab !== "all") params.stage = activeTab;
      if (statusFilter) params.status = statusFilter;
      if (assignmentFilter) params.assignment = assignmentFilter;
      if (search) params.search = search;

      const response = await leadsAPI.getAll(params);
      return response.data || {};
    },
  });

  // Fetch stage stats for counts (all leads, no date filter)
  const { data: stageStats } = useQuery({
    queryKey: ["stageStats"],
    queryFn: async () => {
      const response = await pipelineAPI.getStats({ range: 'all' });
      return response.data?.data || [];
    },
  });

  // Legacy: Fetch leads counts (for backward compatibility)
  const { data: countsData } = useQuery({
    queryKey: ["leadsCounts"],
    queryFn: async () => {
      const response = await leadsAPI.getCounts();
      return response.data?.data || {};
    },
  });

  // Fetch all leads for duplicate checking (cached)
  const { data: allLeadsData } = useQuery({
    queryKey: ["allLeadsForValidation"],
    queryFn: async () => {
      const response = await leadsAPI.getAll({ limit: 10000 });
      return response.data?.data || [];
    },
    staleTime: 30000, // Cache for 30 seconds
  });

  // Get existing phone numbers for duplicate check
  const existingPhones = useMemo(() => {
    if (!allLeadsData) return new Set();
    return new Set(
      allLeadsData.map((lead) =>
        lead.phone?.replace(/[\s\-]/g, "").replace(/^\+91/, ""),
      ),
    );
  }, [allLeadsData]);

  // Fetch total leads count with current filters
  const { data: filteredCountData } = useQuery({
    queryKey: [
      "filteredLeadsCount",
      activeTab,
      statusFilter,
      assignmentFilter,
      search,
    ],
    queryFn: async () => {
      const params = { limit: 1 };
      if (activeTab && activeTab !== "all") params.stage = activeTab;
      if (statusFilter) params.status = statusFilter;
      if (assignmentFilter) params.assignment = assignmentFilter;
      if (search) params.search = search;

      const response = await leadsAPI.getAll(params);
      return response.data?.pagination?.total || 0;
    },
  });

  // Fetch users for assignment
  const { data: usersData } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const response = await usersAPI.getAll({ role: "telecaller" });
      return response.data?.data || [];
    },
  });

  // Delete lead mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => leadsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["leadsCounts"]);
      toast.success("Lead deleted successfully");
    },
    onError: () => toast.error("Failed to delete lead"),
  });

  // Bulk delete leads mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: (ids) => leadsAPI.bulkDelete(ids),
    onSuccess: (response) => {
      const deletedCount =
        response?.data?.deletedCount ?? selectedLeads.length;
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["leadsCounts"]);
      queryClient.invalidateQueries(["stageStats"]);
      toast.success(`${deletedCount} leads deleted successfully`);
      setSelectedLeads([]);
      setSelectionMode("page");
    },
    onError: (error) =>
      toast.error(
        error?.response?.data?.message || "Failed to delete leads",
      ),
  });

  // Bulk update stage mutation
  const bulkUpdateStageMutation = useMutation({
    mutationFn: ({ ids, stage }) => leadsAPI.bulkUpdate({ ids, stage }),
    onSuccess: () => {
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["stageStats"]);
      toast.success("Stage updated successfully");
      setSelectedLeads([]);
      setSelectionMode("page");
      setShowBulkStageModal(false);
    },
    onError: (error) =>
      toast.error(error?.response?.data?.message || "Failed to update stage"),
  });

  // Bulk update status mutation
  const bulkUpdateStatusMutation = useMutation({
    mutationFn: ({ ids, status }) => leadsAPI.bulkUpdate({ ids, status }),
    onSuccess: () => {
      queryClient.invalidateQueries(["leads"]);
      toast.success("Status updated successfully");
      setSelectedLeads([]);
      setSelectionMode("page");
      setShowBulkStatusModal(false);
    },
    onError: (error) =>
      toast.error(error?.response?.data?.message || "Failed to update status"),
  });

  const leads = leadsData?.data || [];
  const pagination = leadsData?.pagination || {};

  // Calculate total leads from stage stats
  const totalLeadsFromStages =
    stageStats?.reduce((sum, stage) => sum + (stage.leadsCount || 0), 0) || 0;

  // Create tabs from stage stats
  const tabs = [
    { value: "all", label: "All", count: totalLeadsFromStages },
    ...(stageStats || []).slice(0, 6).map((stage) => ({
      value: stage.key,
      label: stage.name,
      count: stage.leadsCount || 0,
      color: stage.color,
    })),
  ];

  // Stage filtering is now done on the backend (activeTab -> params.stage),
  // so the loaded page already contains only the matching leads. No
  // client-side re-filtering needed.
  const filteredLeads = leads;

  const handleSelectLead = (id) => {
    setSelectedLeads((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map((l) => l._id));
    }
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this lead?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleBulkDelete = () => {
    if (selectedLeads.length === 0) {
      toast.error("Select leads to delete");
      return;
    }
    if (
      window.confirm(
        `Delete ${selectedLeads.length} lead${selectedLeads.length > 1 ? "s" : ""}? This cannot be undone.`,
      )
    ) {
      bulkDeleteMutation.mutate(selectedLeads);
    }
  };

  const handleSingleAssign = (lead) => {
    setSelectedLeadForAssign(lead);
    setShowSingleAssignModal(true);
  };

  // Export leads to CSV
  const handleExportLeads = () => {
    try {
      const dataToExport = filteredLeads.length > 0 ? filteredLeads : leads;

      if (dataToExport.length === 0) {
        toast.error("No leads to export");
        return;
      }

      // Create CSV headers
      const headers = [
        "Name",
        "Phone",
        "Email",
        "Stage",
        "Status",
        "Created At",
        "Notes",
      ];

      // Create CSV rows
      const csvRows = [
        headers.join(","),
        ...dataToExport.map((lead) =>
          [
            `"${lead.name || ""}"`,
            `"${lead.phone || ""}"`,
            `"${lead.email || ""}"`,
            `"${lead.stage || ""}"`,
            `"${lead.status || ""}"`,
            `"${lead.createdAt ? format(new Date(lead.createdAt), "yyyy-MM-dd HH:mm") : ""}"`,
            `"${(lead.notes || "").replace(/"/g, '""')}"`,
          ].join(","),
        ),
      ];

      // Add summary section
      csvRows.unshift("");
      csvRows.unshift(`Total Leads: ${dataToExport.length}`);
      csvRows.unshift(`Export Date: ${new Date().toLocaleString()}`);
      csvRows.unshift("");
      csvRows.unshift("=== LEADS EXPORT ===");
      csvRows.unshift("");

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `leads-export-${new Date().toISOString().split("T")[0]}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      toast.success(`Exported ${dataToExport.length} leads successfully`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export leads");
    }
  };

  // Parse CSV file
  const parseCSV = (text) => {
    const lines = text.trim().split("\n");
    if (lines.length < 2) {
      toast.error("CSV file must have headers and at least one data row");
      return [];
    }

    const headers = lines[0].split(",").map((h) => h.trim().replace(/"/g, ""));
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(",").map((v) => v.trim().replace(/"/g, ""));
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || "";
      });
      if (row.name || row.Name || row.NAME) {
        data.push(row);
      }
    }

    return data;
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      toast.error("Please upload a CSV file");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const parsed = parseCSV(text);

      if (parsed.length === 0) {
        toast.error("No valid leads found in CSV");
        return;
      }

      // Validate each lead
      const validatedLeads = parsed.map((lead, index) => {
        const errors = [];
        const rawPhone =
          lead.phone ||
          lead.Phone ||
          lead.PHONE ||
          lead.mobile ||
          lead.Mobile ||
          "";
        const rawName = lead.name || lead.Name || lead.NAME || "";
        const rawEmail = lead.email || lead.Email || lead.EMAIL || "";
        const rawStatus = lead.status || lead.Status || lead.STATUS || "";

        // Validate name
        const nameValidation = validateName(rawName);
        if (!nameValidation.isValid) {
          errors.push(`Name: ${nameValidation.error}`);
        }

        // Validate phone
        const phoneValidation = validatePhoneNumber(rawPhone);
        if (!phoneValidation.isValid) {
          errors.push(`Phone: ${phoneValidation.error}`);
        }

        // Validate email if provided
        const emailValidation = validateEmail(rawEmail);
        if (!emailValidation.isValid) {
          errors.push(`Email: ${emailValidation.error}`);
        }

        // Validate stage (was previously status)
        const stageValidation = validateStage(rawStatus);
        if (!stageValidation.isValid) {
          errors.push(`Stage: ${stageValidation.error}`);
        }

        return {
          ...lead,
          rowNum: index + 2,
          name: nameValidation.isValid ? nameValidation.cleanName : rawName,
          phone: phoneValidation.isValid
            ? phoneValidation.cleanPhone
            : rawPhone,
          rawPhone: rawPhone,
          email: emailValidation.isValid
            ? emailValidation.cleanEmail
            : rawEmail,
          stage: stageValidation.isValid
            ? stageValidation.cleanStage
            : rawStatus,
          rawStatus: rawStatus,
          isValid: errors.length === 0,
          errors,
          isDuplicateInFile: false,
          isDuplicateInSystem: false,
        };
      });

      // Check for duplicates within file
      const phoneCounts = {};
      validatedLeads.forEach((lead) => {
        if (lead.phone && lead.isValid) {
          phoneCounts[lead.phone] = (phoneCounts[lead.phone] || 0) + 1;
        }
      });

      // Mark duplicates within file
      validatedLeads.forEach((lead) => {
        if (lead.phone && phoneCounts[lead.phone] > 1) {
          lead.isDuplicateInFile = true;
          lead.isValid = false;
          if (!lead.errors.includes("Duplicate phone in file")) {
            lead.errors.push("Duplicate phone in file");
          }
        }
      });

      // Check for duplicates in system
      validatedLeads.forEach((lead) => {
        if (lead.phone && lead.isValid && existingPhones.has(lead.phone)) {
          lead.isDuplicateInSystem = true;
          lead.isValid = false;
          if (!lead.errors.includes("Phone already exists in system")) {
            lead.errors.push("Phone already exists in system");
          }
        }
      });

      setImportData(validatedLeads);

      // Calculate validation summary
      const validCount = validatedLeads.filter((l) => l.isValid).length;
      const invalidCount = validatedLeads.length - validCount;

      if (invalidCount > 0) {
        setValidationResults({
          total: validatedLeads.length,
          valid: validCount,
          invalid: invalidCount,
          duplicates: validatedLeads.filter((l) => l.isDuplicateInSystem)
            .length,
          duplicateInFile: validatedLeads.filter((l) => l.isDuplicateInFile)
            .length,
        });
        toast.error(
          `Found ${invalidCount} invalid/duplicate leads. Please review before importing.`,
        );
      } else {
        setValidationResults(null);
        toast.success(`Found ${validCount} valid leads ready to import`);
      }
    };
    reader.readAsText(file);
  };

  // Handle import
  const handleImport = async () => {
    if (importData.length === 0) {
      toast.error("No data to import");
      return;
    }

    // Only import valid leads
    const validLeads = importData.filter((lead) => lead.isValid);

    if (validLeads.length === 0) {
      toast.error("No valid leads to import. Please fix errors and try again.");
      return;
    }

    setImporting(true);
    try {
      const response = await leadsAPI.importLeads({
        leads: validLeads.map((lead) => ({
          name: lead.name,
          phone: lead.phone,
          email: lead.email,
          stage: lead.stage || "new_lead",
          status: lead.status || lead.Status || "new",
          notes: lead.notes || lead.Notes,
        })),
        distributeEqually: distributeEqually,
        productIds: importProductIds.length > 0 ? importProductIds : undefined,
      });

      if (response.data?.success) {
        const { totalImported, telecallerCount, totalSkipped } =
          response.data.data;
        let message = `${totalImported} leads imported`;
        if (distributeEqually && telecallerCount) {
          message += ` and distributed to ${telecallerCount} telecallers`;
        }
        if (totalSkipped > 0) {
          message += `. ${totalSkipped} leads were skipped due to validation errors.`;
        }
        toast.success(message);
        setShowImportModal(false);
        setImportData([]);
        setImportProductIds([]);
        setValidationResults(null);
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["leadsCounts"]);
        queryClient.invalidateQueries(["allLeadsForValidation"]);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData?.data?.errors) {
        // Display validation errors from backend
        const errorMessages = errorData.data.errors
          .slice(0, 5)
          .map((e) => `Row ${e.row}: ${e.errors.join(", ")}`)
          .join("\n");
        toast.error(`${errorData.message}\n${errorMessages}`);
      } else {
        toast.error(errorData?.message || "Failed to import leads");
      }
    } finally {
      setImporting(false);
    }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
              Leads
            </h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5 sm:mt-1">
              Manage and assign leads to telecallers
            </p>
          </div>
          <div className="flex gap-1.5 sm:gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              icon={<HiOutlineClipboardCheck className="w-4 h-4" />}
              onClick={() => setShowBulkSelectModal(true)}
            >
              <span className="hidden sm:inline">Bulk Select</span>
              <span className="sm:hidden">Bulk</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              icon={<HiOutlineDownload className="w-4 h-4" />}
              onClick={handleExportLeads}
            >
              <span className="hidden sm:inline">Export</span>
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={<HiOutlinePlus className="w-4 h-4" />}
              onClick={() => navigate("/app/leads/assign")}
            >
              <span className="hidden sm:inline">Add Lead</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

        {/* Filters */}
        <Card padding="p-3 sm:p-4">
          <div className="flex flex-col justify-between sm:flex-row gap-2 sm:gap-3">
            <div className="flex-1 max-w-[800px]">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search leads..."
              />
            </div>
            <div className="flex gap-2">
              <Select
                options={[{ value: "all", label: "All Stages" }, ...stageOptions]}
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
                className="flex-1 min-w-[150px]"
              />
              <Select
                options={[
                  { value: "", label: "All Status" },
                  ...statusOptions,
                ]}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="flex-1 min-w-[120px]"
              />
              <Select
                options={[
                  { value: "", label: "All Assignments" },
                  { value: "assigned", label: "Assigned" },
                  { value: "unassigned", label: "Unassigned" },
                ]}
                value={assignmentFilter}
                onChange={(e) => setAssignmentFilter(e.target.value)}
                className="flex-1 min-w-[150px]"
              />
              <Select
                options={[
                  { value: 25, label: "25" },
                  { value: 50, label: "50" },
                  { value: 100, label: "100" },
                  { value: 200, label: "200" },
                ]}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="flex-1 min-w-[70px]"
              />
            </div>
          </div>
        </Card>

        {/* Bulk Actions */}
        {(selectedLeads.length > 0 || selectionMode === "count") && (
          <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-primary-50 rounded-xl animate-fadeIn">
            <span className="text-xs sm:text-sm font-medium text-primary-700">
              {selectionMode === "count"
                ? `${bulkSelectCount} leads (by count)`
                : `${selectedLeads.length} selected`}
            </span>
            <Button
              variant="primary"
              size="sm"
              icon={<HiOutlineUserAdd className="w-4 h-4" />}
              onClick={() => setShowAssignModal(true)}
            >
              <span className="hidden sm:inline">Assign</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowBulkStageModal(true)}
              disabled={selectedLeads.length === 0}
            >
              <span className="hidden sm:inline">Stage</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowBulkStatusModal(true)}
              disabled={selectedLeads.length === 0}
            >
              <span className="hidden sm:inline">Status</span>
            </Button>
            <Button
              variant="danger"
              size="sm"
              icon={<HiOutlineTrash className="w-4 h-4" />}
              onClick={handleBulkDelete}
              disabled={bulkDeleteMutation.isPending || selectedLeads.length === 0}
            >
              <span className="hidden sm:inline">
                {bulkDeleteMutation.isPending ? "Deleting..." : "Delete"}
              </span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedLeads([]);
                setSelectionMode("page");
              }}
            >
              Clear
            </Button>
          </div>
        )}

        {/* Leads List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : filteredLeads.length === 0 ? (
          <Card>
            <EmptyState
              icon={<HiOutlineUserAdd className="w-8 h-8 text-gray-400" />}
              title="No leads found"
              description="Add leads or adjust your filters"
              action={
                <Button
                  variant="primary"
                  onClick={() => navigate("/app/leads/assign")}
                >
                  <div className="flex items-center">
                    <div>
                      <HiOutlinePlus className="w-4 h-4 mr-1" />
                    </div>
                    <div>Add Lead</div>
                  </div>
                </Button>
              }
            />
          </Card>
        ) : (
          <Card padding="p-0">
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow hoverable={false}>
                    <TableCell header>
                      <input
                        type="checkbox"
                        checked={selectedLeads.length === leads.length}
                        onChange={handleSelectAll}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600"
                      />
                    </TableCell>
                    <TableCell header>Name</TableCell>
                    <TableCell header>Phone</TableCell>
                    <TableCell header>Assigned To</TableCell>
                    <TableCell header>Stage</TableCell>
                    <TableCell header>Status</TableCell>
                    <TableCell header>Created</TableCell>
                    <TableCell header>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLeads.map((lead) => (
                    <TableRow
                      key={lead._id}
                      onClick={() => navigate(`/app/leads/${lead._id}`)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedLeads.includes(lead._id)}
                          onChange={() => handleSelectLead(lead._id)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 cursor-pointer"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-gray-900">
                          {lead.name}
                        </div>
                        {lead.email && (
                          <div className="text-xs text-gray-500">
                            {lead.email}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600">{lead.phone}</span>
                      </TableCell>
                      <TableCell>
                        {lead.assignedTo ? (
                          <span className="text-sm text-gray-700">
                            {lead.assignedTo.firstName}{" "}
                            {lead.assignedTo.lastName}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400 italic">
                            Unassigned
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize"
                          style={{
                            backgroundColor: stageMap[lead.stage]?.color
                              ? `${stageMap[lead.stage].color}20`
                              : "#6B728020",
                            color: stageMap[lead.stage]?.color || "#6B7280",
                          }}
                        >
                          {stageMap[lead.stage]?.name ||
                            lead.stage?.replace(/_/g, " ") ||
                            "Unknown"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`text-sm font-medium status-${lead.status}`}
                        >
                          {lead.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-500 text-sm">
                          {lead.createdAt
                            ? format(new Date(lead.createdAt), "MMM d, yyyy")
                            : "-"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div
                          className="flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<HiOutlineUserAdd className="w-4 h-4" />}
                            onClick={() => handleSingleAssign(lead)}
                            title={
                              lead.assignedTo ? "Reassign Lead" : "Assign Lead"
                            }
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<HiOutlinePencil className="w-4 h-4" />}
                            onClick={() => navigate(`/app/leads/${lead._id}`)}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={
                              <HiOutlineTrash className="w-4 h-4 text-red-500" />
                            }
                            onClick={() => handleDelete(lead._id)}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-100">
              {filteredLeads.map((lead) => (
                <div
                  key={lead._id}
                  className="p-3 sm:p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/app/leads/${lead._id}`)}
                >
                  {/* Header Row */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div
                      className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selectedLeads.includes(lead._id)}
                        onChange={() => handleSelectLead(lead._id)}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600 mt-0.5 cursor-pointer flex-shrink-0"
                      />
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {lead.name}
                        </p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <HiOutlinePhone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          <span className="text-xs sm:text-sm text-gray-500 truncate">
                            {lead.phone}
                          </span>
                        </div>
                        {lead.email && (
                          <p className="text-xs text-gray-400 truncate">
                            {lead.email}
                          </p>
                        )}
                      </div>
                    </div>
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize"
                      style={{
                        backgroundColor: stageMap[lead.stage]?.color
                          ? `${stageMap[lead.stage].color}20`
                          : "#6B728020",
                        color: stageMap[lead.stage]?.color || "#6B7280",
                      }}
                    >
                      {stageMap[lead.stage]?.name ||
                        lead.stage?.replace(/_/g, " ") ||
                        "Unknown"}
                    </span>
                  </div>

                  {/* Info Row */}
                  <div className="pl-6 sm:pl-7 flex items-center justify-between text-xs sm:text-sm">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className={`font-medium status-${lead.status}`}>
                        {lead.status}
                      </span>
                      {lead.assignedTo && (
                        <span className="text-gray-500 truncate">
                          • {lead.assignedTo.firstName}{" "}
                          {lead.assignedTo.lastName}
                        </span>
                      )}
                    </div>
                    <span className="text-gray-400 flex-shrink-0">
                      {lead.createdAt
                        ? format(new Date(lead.createdAt), "MMM d")
                        : "-"}
                    </span>
                  </div>

                  {/* Actions Row */}
                  <div
                    className="flex items-center justify-end gap-1 pt-2 mt-2 border-t border-gray-100"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2"
                      icon={
                        <HiOutlineUserAdd className="w-4 h-4 text-gray-500" />
                      }
                      onClick={() => handleSingleAssign(lead)}
                      title={lead.assignedTo ? "Reassign Lead" : "Assign Lead"}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2"
                      icon={
                        <HiOutlinePencil className="w-4 h-4 text-gray-500" />
                      }
                      onClick={() => navigate(`/app/leads/${lead._id}`)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2"
                      icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                      onClick={() => handleDelete(lead._id)}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 sm:p-4 border-t border-gray-100">
                <span className="text-xs sm:text-sm text-gray-500">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <span className="hidden sm:inline">Previous</span>
                    <span className="sm:hidden">Prev</span>
                  </Button>

                  {buildPageList(pagination.page, pagination.totalPages).map(
                    (item, idx) =>
                      item === "..." ? (
                        <span
                          key={`ellipsis-${idx}`}
                          className="px-2 text-sm text-gray-400 select-none"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={item}
                          type="button"
                          onClick={() => setPage(item)}
                          className={
                            page === item
                              ? "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-semibold bg-primary-600 text-white transition-colors"
                              : "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                          }
                        >
                          {item}
                        </button>
                      )
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Assign Modal */}
        <AssignModal
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          selectedLeads={selectedLeads}
          leads={leads}
          users={usersData || []}
          selectionMode={selectionMode}
          bulkSelectCount={bulkSelectCount}
          filters={{
            stage: activeTab !== "all" ? activeTab : "",
            status: statusFilter,
            search,
            activeTab,
          }}
          onSuccess={() => {
            setSelectedLeads([]);
            setSelectionMode("page");
            setShowAssignModal(false);
            queryClient.invalidateQueries(["leads"]);
            toast.success("Leads assigned successfully");
          }}
        />

        {/* Single Assign Modal */}
        <SingleAssignModal
          isOpen={showSingleAssignModal}
          onClose={() => {
            setShowSingleAssignModal(false);
            setSelectedLeadForAssign(null);
          }}
          lead={selectedLeadForAssign}
          users={usersData || []}
          onSuccess={() => {
            setShowSingleAssignModal(false);
            setSelectedLeadForAssign(null);
            queryClient.invalidateQueries(["leads"]);
            toast.success("Lead assigned successfully");
          }}
        />

        {/* Import Modal */}
        <Modal
          isOpen={showImportModal}
          onClose={() => {
            setShowImportModal(false);
            setImportData([]);
            setValidationResults(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }}
          title="Import Leads"
          size="lg"
        >
          <div className="space-y-4 sm:space-y-6">
            {/* File Upload */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                Upload CSV File
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg sm:rounded-xl p-4 sm:p-6 text-center hover:border-primary-500 transition-colors">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <HiOutlineDocumentText className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-xs sm:text-sm text-gray-600">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    CSV file with columns: name, phone, email (optional)
                  </p>
                </label>
              </div>
            </div>

            {/* Validation Summary */}
            {validationResults && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
                <div className="flex items-center gap-2 text-amber-700 font-medium mb-2 text-sm sm:text-base">
                  <HiOutlineExclamation className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Validation Summary</span>
                </div>
                <div className="text-xs sm:text-sm text-amber-600 space-y-1">
                  <p>
                    Total leads in file:{" "}
                    <strong>{validationResults.total}</strong>
                  </p>
                  <p className="text-green-600">
                    Valid leads: <strong>{validationResults.valid}</strong>
                  </p>
                  {validationResults.duplicateInFile > 0 && (
                    <p className="text-red-600">
                      Duplicates within file:{" "}
                      <strong>{validationResults.duplicateInFile}</strong>
                    </p>
                  )}
                  {validationResults.duplicates > 0 && (
                    <p className="text-red-600">
                      Already in system:{" "}
                      <strong>{validationResults.duplicates}</strong>
                    </p>
                  )}
                  <p className="text-amber-600">
                    Invalid leads: <strong>{validationResults.invalid}</strong>
                  </p>
                </div>
              </div>
            )}

            {/* Preview */}
            {importData.length > 0 && !validationResults && (
              <div className="bg-green-50 border border-green-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
                <div className="flex items-center gap-2 text-green-700 mb-2 text-sm sm:text-base">
                  <HiOutlineCheck className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="font-medium">
                    {importData.length} leads ready to import
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-green-600">
                  Sample: {importData[0]?.name || importData[0]?.Name} -{" "}
                  {importData[0]?.phone || importData[0]?.Phone}
                </p>
              </div>
            )}

            {/* Leads Preview with Validation */}
            {importData.length > 0 && validationResults && (
              <div className="max-h-48 sm:max-h-60 overflow-y-auto space-y-2">
                <p className="text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Lead Preview:
                </p>
                {importData.slice(0, 20).map((lead, index) => (
                  <div
                    key={index}
                    className={`flex items-start justify-between p-2 sm:p-3 rounded-lg border ${
                      lead.isValid
                        ? "bg-green-50 border-green-200"
                        : "bg-red-50 border-red-200"
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {lead.isValid ? (
                          <HiOutlineCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                        ) : (
                          <HiOutlineX className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-500 flex-shrink-0" />
                        )}
                        <span className="font-medium text-gray-900 text-xs sm:text-sm truncate">
                          Row {lead.rowNum}: {lead.name || "No Name"}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 ml-5 sm:ml-6 flex flex-wrap gap-x-2">
                        <span>{lead.rawPhone || lead.phone}</span>
                        {lead.status && (
                          <span className="text-blue-600">• {lead.status}</span>
                        )}
                      </div>
                      {lead.errors && lead.errors.length > 0 && (
                        <div className="mt-1 ml-5 sm:ml-6">
                          {lead.errors.map((error, idx) => (
                            <p key={idx} className="text-xs text-red-500">
                              {error}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {importData.length > 20 && (
                  <p className="text-xs text-gray-500 text-center">
                    ...and {importData.length - 20} more leads
                  </p>
                )}
              </div>
            )}

            {/* Distribution Option */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
              <label className="flex items-center gap-2 sm:gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={distributeEqually}
                  onChange={(e) => setDistributeEqually(e.target.checked)}
                  className="w-4 h-4 sm:w-5 sm:h-5 rounded border-blue-300 text-primary-600"
                />
                <div>
                  <div className="flex items-center gap-2 text-blue-800 font-medium text-sm sm:text-base">
                    <HiOutlineUsers className="w-4 h-4 sm:w-5 sm:h-5" />
                    Distribute equally to all telecallers
                  </div>
                  <p className="text-xs sm:text-sm text-blue-600 mt-1">
                    Leads will be automatically assigned evenly among{" "}
                    {usersData?.length || 0} telecallers
                  </p>
                </div>
              </label>
            </div>

            {/* Products (Optional) - applied to all imported leads */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                <HiOutlineCube className="w-4 h-4" />
                Products (Optional)
              </label>
              {products.length === 0 ? (
                <p className="text-xs text-gray-500">No products available</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {products.map((product) => {
                    const selected = importProductIds.includes(product._id);
                    return (
                      <button
                        type="button"
                        key={product._id}
                        onClick={() =>
                          setImportProductIds((prev) =>
                            selected
                              ? prev.filter((id) => id !== product._id)
                              : [...prev, product._id]
                          )
                        }
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 text-sm font-medium transition-all ${
                          selected
                            ? "bg-primary-50 border-primary-400 text-primary-700"
                            : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                        }`}
                      >
                        <HiOutlineCube className="w-4 h-4" />
                        {product.name}
                      </button>
                    );
                  })}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1.5">
                Selected products will be applied to all imported leads
              </p>
            </div>

            {/* CSV Format Help */}
            <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4">
              <h4 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">
                CSV Format Example:
              </h4>
              <code className="text-xs text-gray-600 block bg-gray-100 p-2 rounded overflow-x-auto">
                name,phone,email,stage,status
                <br />
                John Doe,9876543210,john@email.com,new_lead,new
                <br />
                Jane Smith,9876543211,jane@email.com,open
              </code>
              <div className="mt-2 space-y-1">
                <p className="text-xs text-gray-500">
                  <strong>Note:</strong> Phone must be 10 digits starting with
                  6-9 (Indian mobile format)
                </p>
                <p className="text-xs text-gray-500">
                  <strong>Stage values:</strong> new_lead, contact_attempted,
                  connected, qualified, follow_up_required, meeting_scheduled,
                  proposal_sent, negotiation, converted, lost, not_interested
                </p>
                <p className="text-xs text-gray-500">
                  <strong>Status values:</strong> new, open, in_progress, follow_up, cold, warm, hot
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4 border-t border-gray-100">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowImportModal(false);
                  setImportData([]);
                  setValidationResults(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                }}
              >
                Cancel
              </Button>
              {validationResults && validationResults.invalid > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleImport}
                  loading={importing}
                  disabled={importData.filter((l) => l.isValid).length === 0}
                >
                  Import Valid ({validationResults.valid})
                </Button>
              )}
              <Button
                variant="primary"
                size="sm"
                onClick={handleImport}
                loading={importing}
                disabled={importData.filter((l) => l.isValid).length === 0}
              >
                {validationResults && validationResults.invalid > 0
                  ? `Import ${validationResults.valid}`
                  : `Import ${importData.length}`}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Bulk Select Modal */}
        <BulkSelectModal
          isOpen={showBulkSelectModal}
          onClose={() => setShowBulkSelectModal(false)}
          totalLeads={filteredCountData || 0}
          currentFilters={{
            stage: activeTab !== "all" ? activeTab : "",
            status: statusFilter,
            search,
            activeTab,
          }}
          onConfirm={(count) => {
            setBulkSelectCount(count);
            setSelectionMode("count");
            setSelectedLeads([]);
            setShowBulkSelectModal(false);
          }}
        />

        {/* Bulk Stage Modal */}
        <BulkStageModal
          isOpen={showBulkStageModal}
          onClose={() => setShowBulkStageModal(false)}
          selectedLeads={selectedLeads}
          stages={stages}
          onConfirm={(stage) => {
            bulkUpdateStageMutation.mutate({ ids: selectedLeads, stage });
          }}
          isLoading={bulkUpdateStageMutation.isPending}
        />

        {/* Bulk Status Modal */}
        <BulkStatusModal
          isOpen={showBulkStatusModal}
          onClose={() => setShowBulkStatusModal(false)}
          selectedLeads={selectedLeads}
          onConfirm={(status) => {
            bulkUpdateStatusMutation.mutate({ ids: selectedLeads, status });
          }}
          isLoading={bulkUpdateStatusMutation.isPending}
        />
      </div>
    </Layout>
  );
};

// Assign Modal Component
const AssignModal = ({
  isOpen,
  onClose,
  selectedLeads,
  leads,
  users,
  onSuccess,
  selectionMode,
  bulkSelectCount,
  filters,
}) => {
  const [selectedUser, setSelectedUser] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate assignment stats for selected leads
  const getAssignmentStats = () => {
    if (selectionMode === "count" || !leads) {
      return {
        unassigned: 0,
        assigned: 0,
        assignedTo: null,
        assignedNames: [],
      };
    }

    const selectedLeadsData = leads.filter((l) =>
      selectedLeads.includes(l._id),
    );
    const unassigned = selectedLeadsData.filter((l) => !l.assignedTo).length;
    const assigned = selectedLeadsData.filter((l) => l.assignedTo).length;

    // Get unique assigned users
    const assignedUsers = {};
    selectedLeadsData.forEach((l) => {
      if (l.assignedTo?._id) {
        assignedUsers[l.assignedTo._id] =
          `${l.assignedTo.firstName} ${l.assignedTo.lastName}`;
      }
    });
    const assignedNames = Object.values(assignedUsers);

    // If all assigned to same person
    const allAssignedToSame =
      assigned > 0 && Object.keys(assignedUsers).length === 1;
    const assignedTo = allAssignedToSame ? Object.keys(assignedUsers)[0] : null;

    return { unassigned, assigned, assignedTo, assignedNames };
  };

  const stats = getAssignmentStats();

  const handleAssign = async () => {
    if (!selectedUser) {
      toast.error("Select a user to assign");
      return;
    }

    setIsSubmitting(true);
    try {
      if (selectionMode === "count") {
        // Assign by count with filters
        await leadsAPI.assignByFilters({
          assignedTo: selectedUser,
          count: bulkSelectCount,
          stage: filters?.stage,
          status: filters?.status,
          search: filters?.search,
          activeTab: filters?.activeTab,
        });
      } else {
        // Assign by specific lead IDs
        await leadsAPI.bulkAssign({
          leadIds: selectedLeads,
          assignedTo: selectedUser,
        });
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to assign leads");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Pre-select the current assignee if all leads are assigned to same person
  React.useEffect(() => {
    if (stats.assignedTo) {
      setSelectedUser(stats.assignedTo);
    } else {
      setSelectedUser("");
    }
  }, [stats.assignedTo]);

  if (!isOpen) return null;

  const displayCount =
    selectionMode === "count" ? bulkSelectCount : selectedLeads.length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Assign Leads" size="sm">
      <div className="space-y-3 sm:space-y-4">
        <p className="text-gray-600 text-sm">
          Assign <span className="font-semibold">{displayCount}</span> leads to
          a telecaller
        </p>

        {/* Show assignment status */}
        {selectionMode !== "count" && (
          <div className="space-y-2">
            {stats.unassigned > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 sm:p-3">
                <p className="text-xs sm:text-sm text-blue-700">
                  <span className="font-medium">{stats.unassigned}</span>{" "}
                  unassigned leads will be assigned
                </p>
              </div>
            )}
            {stats.assigned > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 sm:p-3">
                <p className="text-xs sm:text-sm text-amber-700">
                  <span className="font-medium">{stats.assigned}</span> leads
                  are already assigned
                  {stats.assignedNames.length > 0 && (
                    <span className="block mt-1">
                      Currently assigned to:{" "}
                      <strong>{stats.assignedNames.join(", ")}</strong>
                    </span>
                  )}
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  These leads will be reassigned to the telecaller you select
                  below.
                </p>
              </div>
            )}
          </div>
        )}

        {selectionMode === "count" && (
          <p className="text-xs text-gray-500">
            Only unassigned leads matching your filters will be assigned.
          </p>
        )}

        <Select
          label="Select Telecaller"
          options={users.map((u) => ({
            value: u._id,
            label: `${u.firstName} ${u.lastName}`,
          }))}
          placeholder="Choose a telecaller"
          value={selectedUser}
          onChange={(e) => setSelectedUser(e.target.value)}
        />
        <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleAssign}
            loading={isSubmitting}
          >
            {stats.assigned > 0 ? "Reassign" : "Assign"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Single Assign Modal Component
const SingleAssignModal = ({ isOpen, onClose, lead, users, onSuccess }) => {
  const [selectedUser, setSelectedUser] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    if (lead?.assignedTo?._id) {
      setSelectedUser(lead.assignedTo._id);
    } else {
      setSelectedUser("");
    }
  }, [lead]);

  const handleAssign = async () => {
    if (!selectedUser) {
      toast.error("Select a user to assign");
      return;
    }

    setIsSubmitting(true);
    try {
      await leadsAPI.update(lead._id, { assignedTo: selectedUser });
      onSuccess();
    } catch (error) {
      toast.error("Failed to assign lead");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !lead) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Assign Lead" size="sm">
      <div className="space-y-3 sm:space-y-4">
        <div className="bg-gray-50 rounded-lg sm:rounded-xl p-2.5 sm:p-3">
          <p className="text-xs text-gray-500">Lead</p>
          <p className="font-medium text-gray-900">{lead.name}</p>
          <p className="text-xs sm:text-sm text-gray-500">{lead.phone}</p>
        </div>
        {lead.assignedTo && (
          <p className="text-xs sm:text-sm text-amber-600">
            Currently assigned to:{" "}
            <strong>
              {lead.assignedTo.firstName} {lead.assignedTo.lastName}
            </strong>
          </p>
        )}
        <Select
          label="Select Telecaller"
          options={users.map((u) => ({
            value: u._id,
            label: `${u.firstName} ${u.lastName}`,
          }))}
          placeholder="Choose a telecaller"
          value={selectedUser}
          onChange={(e) => setSelectedUser(e.target.value)}
        />
        <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleAssign}
            loading={isSubmitting}
          >
            {lead.assignedTo ? "Reassign" : "Assign"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Bulk Select Modal Component
const BulkSelectModal = ({
  isOpen,
  onClose,
  totalLeads,
  currentFilters,
  onConfirm,
}) => {
  const [selectCount, setSelectCount] = useState(50);
  const [customCount, setCustomCount] = useState("");

  const presetOptions = [
    { value: 50, label: "50 leads" },
    { value: 100, label: "100 leads" },
    { value: 200, label: "200 leads" },
    { value: 500, label: "500 leads" },
    { value: -1, label: "All leads" },
    { value: 0, label: "Custom count" },
  ];

  const handleConfirm = () => {
    const finalCount =
      selectCount === 0
        ? parseInt(customCount) || 50
        : selectCount === -1
          ? totalLeads
          : selectCount;

    if (finalCount <= 0) {
      toast.error("Please enter a valid count");
      return;
    }

    if (finalCount > totalLeads) {
      toast.error(`Only ${totalLeads} leads available with current filters`);
      return;
    }

    onConfirm(finalCount);
  };

  if (!isOpen) return null;

  const getFilterDescription = () => {
    const filters = [];
    if (currentFilters?.stage) filters.push(`Stage: ${currentFilters.stage}`);
    if (currentFilters?.status)
      filters.push(`Status: ${currentFilters.status}`);
    if (currentFilters?.search)
      filters.push(`Search: "${currentFilters.search}"`);
    if (currentFilters?.activeTab && currentFilters.activeTab !== "all") {
      filters.push(`Tab: ${currentFilters.activeTab}`);
    }
    return filters.length > 0 ? filters.join(", ") : "No filters applied";
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Bulk Select Leads"
      size="sm"
    >
      <div className="space-y-3 sm:space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
          <div className="flex items-center gap-2 text-blue-700 font-medium mb-1.5 sm:mb-2 text-sm sm:text-base">
            <HiOutlineUsers className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>{totalLeads.toLocaleString()} leads available</span>
          </div>
          <p className="text-xs sm:text-sm text-blue-600">
            {getFilterDescription()}
          </p>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Select how many leads to assign
          </label>
          <Select
            options={presetOptions.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            value={selectCount}
            onChange={(e) => setSelectCount(parseInt(e.target.value))}
          />
        </div>

        {selectCount === 0 && (
          <Input
            label="Enter custom count"
            type="number"
            value={customCount}
            onChange={(e) => setCustomCount(e.target.value)}
            placeholder={`Max ${totalLeads}`}
            min={1}
            max={totalLeads}
          />
        )}

        <div className="bg-gray-50 rounded-lg sm:rounded-xl p-2.5 sm:p-3">
          <p className="text-xs sm:text-sm text-gray-600">
            <strong>Selected:</strong>{" "}
            {selectCount === 0
              ? customCount || 0
              : selectCount === -1
                ? totalLeads
                : selectCount}{" "}
            leads
          </p>
        </div>

        <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleConfirm}>
            Select
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Bulk Stage Modal Component
const BulkStageModal = ({ isOpen, onClose, selectedLeads, stages, onConfirm, isLoading }) => {
  const [selectedStage, setSelectedStage] = useState("");

  const handleConfirm = () => {
    if (!selectedStage) {
      toast.error("Please select a stage");
      return;
    }
    onConfirm(selectedStage);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change Stage" size="sm">
      <div className="space-y-3 sm:space-y-4">
        <p className="text-gray-600 text-sm">
          Change stage for <span className="font-semibold">{selectedLeads.length}</span> selected leads
        </p>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Select New Stage
          </label>
          <Select
            options={[
              { value: "", label: "Choose a stage" },
              ...stages.map((stage) => ({
                value: stage.key,
                label: stage.name,
              })),
            ]}
            value={selectedStage}
            onChange={(e) => setSelectedStage(e.target.value)}
          />
        </div>

        <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            loading={isLoading}
            disabled={!selectedStage}
          >
            Update Stage
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Bulk Status Modal Component
const BulkStatusModal = ({ isOpen, onClose, selectedLeads, onConfirm, isLoading }) => {
  const [selectedStatus, setSelectedStatus] = useState("");

  const statusOptions = [
    { value: "", label: "Choose a status" },
    { value: "new", label: "New" },
    { value: "open", label: "Open" },
    { value: "in_progress", label: "In Progress" },
    { value: "follow_up", label: "Follow Up" },
    { value: "cold", label: "Cold" },
    { value: "warm", label: "Warm" },
    { value: "hot", label: "Hot" },
    { value: "converted", label: "Converted" },
  ];

  const handleConfirm = () => {
    if (!selectedStatus) {
      toast.error("Please select a status");
      return;
    }
    onConfirm(selectedStatus);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change Status" size="sm">
      <div className="space-y-3 sm:space-y-4">
        <p className="text-gray-600 text-sm">
          Change status for <span className="font-semibold">{selectedLeads.length}</span> selected leads
        </p>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Select New Status
          </label>
          <Select
            options={statusOptions}
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          />
        </div>

        <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            loading={isLoading}
            disabled={!selectedStatus}
          >
            Update Status
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Commented out - Bulk Priority Modal not needed
// const BulkPriorityModal = ({ isOpen, onClose, selectedLeads, onConfirm, isLoading }) => {
//   const [selectedPriority, setSelectedPriority] = useState("");
//
//   const handleConfirm = () => {
//     if (!selectedPriority) {
//       toast.error("Please select a priority");
//       return;
//     }
//     onConfirm(selectedPriority);
//   };
//
//   if (!isOpen) return null;
//
//   return (
//     <Modal isOpen={isOpen} onClose={onClose} title="Change Priority" size="sm">
//       <div className="space-y-3 sm:space-y-4">
//         <p className="text-gray-600 text-sm">
//           Change priority for <span className="font-semibold">{selectedLeads.length}</span> selected leads
//         </p>
//
//         <div>
//           <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
//             Select New Priority
//           </label>
//           <Select
//             options={[
//               { value: "", label: "Choose a priority" },
//               ...priorityOptions,
//             ]}
//             value={selectedPriority}
//             onChange={(e) => setSelectedPriority(e.target.value)}
//           />
//         </div>
//
//         <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4">
//           <Button variant="ghost" size="sm" onClick={onClose}>
//             Cancel
//           </Button>
//           <Button
//             variant="primary"
//             size="sm"
//             onClick={handleConfirm}
//             loading={isLoading}
//             disabled={!selectedPriority}
//           >
//             Update Priority
//           </Button>
//         </div>
//       </div>
//     </Modal>
//   );
// };

export default LeadsList;
